<template>
	<div class="transcription-upload-alert">
		<!-- media has been uploaded and is being transcribed -->
		<TeamingAlert
			v-if="isTranscribingAlert"
			class="transcription-status-alert"
			type="SYSTEM"
			variant="HOLLOW"
		>
			<template #icon>
				<TranscribingIcon />
			</template>
			<template #default>
				<div class="alert-body">
					<p>{{ translate( `uploadTranscribingAlert` ) }}</p>
				</div>
			</template>
		</TeamingAlert>

		<!-- media has been uploaded but transcription has failed -->
		<!-- NOTE: this will only be displayed when coming through as an update status; on page load it will not be displayed -->
		<TeamingAlert
			v-else-if="isFailureAlert"
			class="transcription-status-alert"
			type="ERROR"
			variant="HOLLOW"
		>
			<div class="alert-body">
				<p>{{ translate( `uploadErrorAlert` ) }}</p>
			</div>
		</TeamingAlert>

		<div v-else><!-- intentionally empty --></div>
	</div>
</template>

<script>
import debugFactory from "debug";

import {
	EventsMixin,
	TranslateMixin,
	TeamingAlert
} from "@teaming/common-ui";

import {
	RecallAIBotStatuses as BotStatus,
	TranscriptionJobStatus,
	TranscriptionOrigin
} from "@teaming/common-enums";

import TranscribingIcon from "./TranscribingIcon.vue";

const debug = debugFactory( "teaming:common-team:components:TranscriptionUploadAlert" );

export default {
	name: "TranscriptionUploadAlert",
	teamingPackage: "common-team",
	mixins: [ EventsMixin, TranslateMixin, ],
	components: {
		TranscribingIcon,
		TeamingAlert,
	},
	props: {
		subDocId: {
			type: String,
			required: true,
		},
		transcriptionOrigin: {
			type: String,
			required: true,
			validator: value => TranscriptionOrigin.includes( value ) || value === "",
		},
		transcriptionStatus: {
			type: String,
			required: true,
			validator: value => TranscriptionJobStatus.includes( value ) || value === "",
		},
		transcriptionSubStatus: {
			type: String,
			required: true,
			validator: value => BotStatus.includes( value ) || value === "",
		},
		// TODO: determine if needed
		// eslint-disable-next-line vue/no-unused-properties
		hasStatusUpdate: {
			type: Boolean,
			required: true,
		},
	},
	computed: {
		isTranscribingAlert() {
			return [
				TranscriptionJobStatus.REQUESTED,
				TranscriptionJobStatus.REQUESTED_RETRY,
				TranscriptionJobStatus.QUEUED,
				TranscriptionJobStatus.COMMITTED,
				TranscriptionJobStatus.SUMMARIZED,
			].includes( this.transcriptionStatus );
		},
		isFailureAlert() {
			return [
				TranscriptionJobStatus.FAILED,
				TranscriptionJobStatus.ABORTED,
			].includes( this.transcriptionStatus );
		},
	},
	methods: {
		logSelf( lifecycleHook ) {
			const {
				subDocId,
				transcriptionOrigin,
				transcriptionStatus,
				transcriptionSubStatus,
			} = this;
			// eslint-disable-next-line no-console
			debug( [
				`[${ lifecycleHook }]`,
				`  subdoc ${ subDocId }`,
				`  origin ${ transcriptionOrigin }`,
				`  status ${ transcriptionStatus }`,
				`  subStatus ${ transcriptionSubStatus }`,
			].join( "\n" ) );
		},
	},
	mounted() {
		this.logSelf( "mounted" );
	},
	updated() {
		this.logSelf( "updated" );
	},
};
</script>

<style lang="scss" scoped>
$textColor: $color9-500;

.transcription-upload-alert {
	color: $textColor;
}

.transcription-upload-alert .alert-body {
	display: flex;
	flex-direction: column;
	justify-content: flex-start;
	gap: 0.5rem;

	@include anchor() {
		color: $textColor !important;
	}

	& > div {
		display: flex;
		flex-direction: row;
		justify-content: flex-start;
		gap: 0.5rem;
	}
}
</style>
